"use client"

import axios from "axios"
import moment from "moment-timezone"
import { useEffect, useState } from "react"
import DatePicker from "react-datepicker"
import { NotificationManager } from "react-notifications"
import dollar from "../../../assets/icons/dollar.svg"
import percentage from "../../../assets/icons/percentage.svg"
import SearchDropdown from "../../../components/Dropdowns/SearchDropdown"
import InputWithIcon from "../../../components/InputWithIcon"
import useAuth from "../../../hooks/useAuth"
import { BASE_URL } from "../../../utils/Element"

const AddUnderContractData = ({
  addexecutedData,
  handleSubmit,
  isSidebarOpen,
  setExecutedData,
  handleChange,
  onClose,
  disable,
  error,
  onSetError,
  isDollar,
  setIsDollar,
  isSellerBDSDollar,
  setIsSellerBDSDollar,
  isBuyerBDSDollar,
  setIsBuyerBDSDollar,
  isSellerBrokerDollar,
  setIsSellerBrokerDollar,
  isBuyerBrokerDollar,
  setIsBuyerBrokerDollar,
  isEditMode = false, // ← New prop to detect edit mode
}) => {
  const [config] = useAuth()
  const [propertyOptions, setPropertyOptions] = useState([])

  const fetchPropertySuggestions = (inputValue = "") => {
    const isPortfolio = addexecutedData?.portfolio === 1
    axios
      .get(`${BASE_URL}/get-datalisting?list=executed&search=${inputValue}&portfolio=${isPortfolio ? 1 : 0}`, config)
      .then((res) => {
        const data = res.data.executed
        const options = data.map((el) => ({
          value: el.id,
          label: `${el?.link?.property_name} (${el?.offer?.status_name})`,
          desc: `${el?.link?.street_address}, ${el?.link?.city}, ${el?.link?.state}`,
          executed_id: el.id,
          property: {
            id: el?.link?.id,
            name: el?.link?.property_name,
          },
        }))
        setPropertyOptions(options)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
  }

  const handlePortfolioChange = () => {
    const portfolioStatus = addexecutedData.portfolio === 1 ? 0 : 1
    setExecutedData({
      ...addexecutedData,
      portfolio: portfolioStatus,
      property: { id: "", name: "" },
    })
  }

  useEffect(() => {
    if (isSidebarOpen) {
      fetchPropertySuggestions("")
    }
  }, [isSidebarOpen, addexecutedData?.portfolio])

  return (
    <div>
      <form className="contact-form" onSubmit={handleSubmit}>
        {/* Contract Date */}
        <div className="mt-6">
          <label className="dark-H head-4 mb-2">Contract Date</label>
          <DatePicker
            className="body-N w-full border rounded-lg px-3 py-2"
            dateFormat="MM/dd/yyyy"
            placeholderText="MM/DD/YYYY"
            selected={
              addexecutedData.contract_date
                ? moment.tz(addexecutedData.contract_date, "America/New_York").toDate()
                : null
            }
            onChange={(value) => {
              const nyDate = moment(value).tz("America/New_York").format("YYYY-MM-DD")
              onSetError({ ...error, contract_date: "" })
              handleChange(nyDate, "contract_date")
            }}
          />
          {error?.contract_date && <p className="body-S red-D">{error?.contract_date}</p>}
        </div>

        {/* Portfolio */}
        <div className="mt-6">
          <label className="container" style={{ marginBottom: "0px" }}>
            <p style={{ paddingLeft: "30px" }} className="dark-H head-4">Portfolio</p>
            <input
              type="checkbox"
              checked={!!addexecutedData?.portfolio}
              className="body-N"
              onChange={handlePortfolioChange}
            />
            <span className="checkmark"></span>
          </label>
        </div>

        {/* Property Search */}
        <div className="mt-6">
          <label className="dark-H head-4 mb-2">Property</label>
          <SearchDropdown
            placeholder="search Property here"
            text={addexecutedData?.property?.name}
            options={propertyOptions}
            fetchSuggestions={fetchPropertySuggestions}
            onSetOptions={setPropertyOptions}
            handleSelect={(option) => {
              setExecutedData({
                ...addexecutedData,
                executed_id: option?.value,
                property: { id: option?.property?.id, name: option?.label },
              })
              setPropertyOptions([])
            }}
          />
          {error?.property_name && <p className="body-S red-D pt-1">{error?.property_name}</p>}
        </div>

        {/* Contract Price */}
        <div className="mt-6">
          <label className="dark-H head-4 mb-2">Contract Price</label>
          <InputWithIcon
            icon={dollar}
            className="body-N"
            type="number"
            placeholder="Write Contract Price Here"
            value={addexecutedData?.contract_price || ""}
            onChange={(e) => handleChange(e.target.value, "contract_price")}
          />
          {error?.contract_price && <p className="body-S red-D pt-1">{error?.contract_price}</p>}
        </div>

        {/* Contract Cap Rate */}
        <div className="mt-6">
          <label className="dark-H head-4 mb-2">Contract Cap Rate</label>
          <InputWithIcon
            icon={percentage}
            className="body-N"
            type="number"
            placeholder="Write Contract Cap Rate Here"
            value={addexecutedData?.contract_cap_rate || ""}
            onChange={(e) => handleChange(e.target.value, "contract_cap_rate")}
          />
          {error?.contract_cap_rate && <p className="body-S red-D pt-1">{error?.contract_cap_rate}</p>}
        </div>

        {/* GCI To Company */}
        <div className="mt-6">
          <div className="flex justify-between gap-2">
            <label className="dark-H head-4 mb-2">GCI To Company</label>
            <button
              type="button"
              onClick={() => setIsDollar(!isDollar)}
              className="px-3 py-1 text-sm border rounded-lg bg-gray-100 hover:bg-gray-200"
            >
              {isDollar ? "%" : "$"}
            </button>
          </div>
          <InputWithIcon
            icon={isDollar ? dollar : percentage}
            className="body-N"
            type="number"
            placeholder="Write GCI To Company"
            value={addexecutedData.company_gcl || ""}
            onChange={(e) => handleChange(e.target.value, "company_gcl")}
          />
          {error?.company_gcl && <p className="body-S red-D pt-1">{error?.company_gcl}</p>}
        </div>

        {/* Seller BDS - Disabled in Edit Mode */}
        {/* <div className="mt-6">
          <div className="flex justify-between gap-2">
            <label className="dark-H head-4 mb-2">Seller BDS</label>
            {!isEditMode && (
              <button
                type="button"
                onClick={() => setIsSellerBDSDollar(!isSellerBDSDollar)}
                className="px-3 py-1 text-sm border rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                {isSellerBDSDollar ? "%" : "$"}
              </button>
            )}
          </div>
          <div className={isEditMode ? "opacity-60 pointer-events-none" : ""}>
            <InputWithIcon
              icon={isSellerBDSDollar ? dollar : percentage}
              className="body-N"
              type="number"
              placeholder="Write Seller BDS"
              value={addexecutedData.gcl_broker_representing_seller || ""}
              onChange={(e) => handleChange(e.target.value, "gcl_broker_representing_seller")}
              disabled={isEditMode}
            />
          </div>
          {error?.gcl_broker_representing_seller && (
            <p className="body-S red-D pt-1">{error?.gcl_broker_representing_seller}</p>
          )}
        </div> */}

        {/* Buyer BDS - Disabled in Edit Mode */}
        {/* <div className="mt-6">
          <div className="flex justify-between gap-2">
            <label className="dark-H head-4 mb-2">Buyer BDS</label>
            {!isEditMode && (
              <button
                type="button"
                onClick={() => setIsBuyerBDSDollar(!isBuyerBDSDollar)}
                className="px-3 py-1 text-sm border rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                {isBuyerBDSDollar ? "%" : "$"}
              </button>
            )}
          </div>
          <div className={isEditMode ? "opacity-60 pointer-events-none" : ""}>
            <InputWithIcon
              icon={isBuyerBDSDollar ? dollar : percentage}
              className="body-N"
              type="number"
              placeholder="Write Buyer BDS"
              value={addexecutedData.gcl_broker_representing_buyer || ""}
              onChange={(e) => handleChange(e.target.value, "gcl_broker_representing_buyer")}
              disabled={isEditMode}
            />
          </div>
          {error?.gcl_broker_representing_buyer && (
            <p className="body-S red-D pt-1">{error?.gcl_broker_representing_buyer}</p>
          )}
        </div> */}

        {/* Seller Broker - Disabled in Edit Mode */}
        {/* <div className="mt-6">
          <div className="flex justify-between gap-2">
            <label className="dark-H head-4 mb-2">Seller Broker</label>
            {!isEditMode && (
              <button
                type="button"
                onClick={() => setIsSellerBrokerDollar(!isSellerBrokerDollar)}
                className="px-3 py-1 text-sm border rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                {isSellerBrokerDollar ? "%" : "$"}
              </button>
            )}
          </div>
          <div className={isEditMode ? "opacity-60 pointer-events-none" : ""}>
            <InputWithIcon
              icon={isSellerBrokerDollar ? dollar : percentage}
              className="body-N"
              type="number"
              placeholder="Write Seller Broker"
              value={addexecutedData.gcl_bds_seller || ""}
              onChange={(e) => handleChange(e.target.value, "gcl_bds_seller")}
              disabled={isEditMode}
            />
          </div>
          {error?.gcl_bds_seller && <p className="body-S red-D pt-1">{error?.gcl_bds_seller}</p>}
        </div> */}

        {/* Buyer Broker - Disabled in Edit Mode */}
        {/* <div className="mt-6">
          <div className="flex justify-between gap-2">
            <label className="dark-H head-4 mb-2">Buyer Broker</label>
            {!isEditMode && (
              <button
                type="button"
                onClick={() => setIsBuyerBrokerDollar(!isBuyerBrokerDollar)}
                className="px-3 py-1 text-sm border rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                {isBuyerBrokerDollar ? "%" : "$"}
              </button>
            )}
          </div>
          <div className={isEditMode ? "opacity-60 pointer-events-none" : ""}>
            <InputWithIcon
              icon={isBuyerBrokerDollar ? dollar : percentage}
              className="body-N"
              type="number"
              placeholder="Write Buyer Broker"
              value={addexecutedData.gcl_bds_buyer || ""}
              onChange={(e) => handleChange(e.target.value, "gcl_bds_buyer")}
              disabled={isEditMode}
            />
          </div>
          {error?.gcl_bds_buyer && <p className="body-S red-D pt-1">{error?.gcl_bds_buyer}</p>}
        </div> */}

        {/* Buttons */}
        <div className="mt-6">
          <button
            type="submit"
            disabled={disable}
            className="save-button light-L head-5 green-bg-H"
          >
            Save
          </button>
          <button type="button" onClick={onClose} className="green-H ml-5">
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}

export default AddUnderContractData